import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import * as I from 'immutable';
import moment from 'moment';
import React from 'react';

import AnalyzePolygonChart from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import ResizedImage from 'app/components/ResizedImage';
import {ApiOrganization, getAreaUnit} from 'app/modules/Remote/Organization';
import {getAttachmentsByType} from 'app/stores/NotesStore';
import * as firebaseUtils from 'app/utils/firebaseUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {BULLET} from 'app/utils/noteUtils';

import {NoteSlide, StorySlide} from './storySlides';
import cs from './styles.module.scss';

interface Props {
  slide: StorySlide;
  slideNumber: number;
  slideCount: number;
  /** Multi-location stories name the property each slide belongs to. */
  showFeatureName: boolean;
  imageRefs: mapUtils.MapImageRefs;
  organization: I.ImmutableOf<ApiOrganization>;
  onPrevious: () => void;
  onNext: () => void;
  /** Collapsed, the card is a one-line bar so the map underneath can be explored. */
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
}

const StoryCaption = React.forwardRef<HTMLDivElement, Props>(function StoryCaption(
  {
    slide,
    slideNumber,
    slideCount,
    showFeatureName,
    imageRefs,
    organization,
    onPrevious,
    onNext,
    isCollapsed,
    onToggleCollapsed,
  },
  ref
) {
  const [expandedImage, setExpandedImage] = React.useState<string | null>(null);
  const noteSlide = slide.kind === 'note' ? slide : null;
  const chart = noteSlide?.chart ?? null;
  const {images, documents} = getAttachmentsByType(noteSlide?.attachments ?? null);

  return (
    <>
      <div
        ref={ref}
        className={classnames(cs.caption, {
          [cs.withChart]: !!chart && !isCollapsed,
          [cs.collapsed]: isCollapsed,
        })}
      >
        <B.Button
          className={cs.captionNavButton}
          icon="chevron-left"
          minimal
          large
          disabled={slideNumber === 1}
          onClick={onPrevious}
          aria-label="Previous slide"
        />

        {isCollapsed ? (
          <div className={cs.collapsedSummary}>
            <span className={cs.collapsedMeta}>
              {slide.kind === 'cover'
                ? slide.subtitle
                : captionMeta(slide, slideNumber, slideCount, showFeatureName)}
            </span>
            <span className={cs.collapsedTitle}>
              {slide.kind === 'cover' ? slide.title : slide.title || slide.note.text}
            </span>
          </div>
        ) : (
          <div className={cs.captionContent}>
            <div className={cs.captionBody}>
              {slide.kind === 'cover' ? (
                <>
                  <h2 className={cs.coverTitle}>{slide.title}</h2>
                  <div className={cs.coverSubtitle}>{slide.subtitle}</div>
                  <div className={cs.captionMeta}>
                    {[slide.company, slide.date].filter(Boolean).join(` ${BULLET} `)}
                  </div>
                  {slide.paragraphs.map((paragraph, i) => (
                    <p key={i} className={cs.captionText}>
                      {paragraph}
                    </p>
                  ))}
                </>
              ) : (
                <>
                  <div className={cs.captionMeta}>
                    {captionMeta(slide, slideNumber, slideCount, showFeatureName)}
                  </div>
                  {slide.title && <h2 className={cs.captionTitle}>{slide.title}</h2>}
                  <p className={cs.captionText}>{slide.note.text}</p>
                </>
              )}

              {documents.length > 0 && (
                <div className={cs.captionDocuments}>
                  {documents.map((url) => (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer">
                      {firebaseUtils.getFileNameFromObjectDownloadUrl(url)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {chart && (
              <div className={cs.captionChart}>
                <AnalyzePolygonChart
                  graph={chart.graph}
                  imageRefs={imageRefs}
                  areaInM2={chart.areaInM2}
                  areaUnit={getAreaUnit(organization)}
                  disablePanZoom
                  showTitle
                  isLegendInteractive={false}
                  graphStyle={{height: 140}}
                  isExpanded={false}
                />
              </div>
            )}

            {images.length > 0 && (
              <div className={cs.captionThumbs}>
                {images.map((url) => (
                  <button
                    key={url}
                    type="button"
                    className={cs.thumb}
                    onClick={() => setExpandedImage(url)}
                    aria-label="Expand photo"
                  >
                    <ResizedImage imageUrl={url} dimension={300} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <B.Button
          className={cs.collapseButton}
          icon={isCollapsed ? 'maximize' : 'minimize'}
          minimal
          onClick={onToggleCollapsed}
          aria-label={isCollapsed ? 'Expand caption' : 'Minimize caption'}
        />

        <B.Button
          className={cs.captionNavButton}
          icon="chevron-right"
          minimal
          large
          disabled={slideNumber === slideCount}
          onClick={onNext}
          aria-label="Next slide"
        />
      </div>

      <B.Dialog
        isOpen={expandedImage !== null}
        onClose={() => setExpandedImage(null)}
        title={slide.title || 'Photo'}
        className={cs.expandedDialog}
      >
        <div className={B.Classes.DIALOG_BODY}>
          {expandedImage && (
            <div className={cs.expandedImage}>
              <ResizedImage imageUrl={expandedImage} dimension={1500} />
            </div>
          )}
        </div>
      </B.Dialog>
    </>
  );
});

function captionMeta(
  slide: NoteSlide,
  slideNumber: number,
  slideCount: number,
  showFeatureName: boolean
) {
  const parts: (string | null)[] = [`${slideNumber} of ${slideCount}`];

  if (showFeatureName) {
    // Parts of one property share a name, so the part name is what tells them apart.
    parts.push(
      (slide.feature.getIn(['properties', 'multiFeaturePartName']) ||
        slide.feature.getIn(['properties', 'name'])) as string
    );
  }

  parts.push(slide.note.name, moment(slide.note.createdAt).format('LL'));

  return parts.filter(Boolean).join(` ${BULLET} `);
}

export default StoryCaption;
